/**
 * Время прошедшее с даты
 * ====================================
 */
import { LoaderFunctionArgs, MetaDescriptor, MetaFunction, useLoaderData, useLocation } from "react-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";
import ToolForm from "~/components/tool-page/ToolForm";
import { DateDiff } from "~/helpers/dates.server";
import { getDatesDiff } from "~/helpers/dates";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import DatePicker from "../../../components/forms/DatePicker";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import FormGroup from "~/components/FormGroup";
import clsx from "clsx";
import TextInput from "~/components/forms/TextInput";
import Label from "~/components/Label";
import SelectInput from "~/components/forms/SelectInput";
import TimeSince from "~/components/TimeSince";
import ToggleInput from "~/components/forms/ToggleInput";
import Button from "~/components/Button";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import { addNoindexMeta } from "~/helpers/meta-noindex";
import H2 from "~/components/H2";
import Author from "~/components/tool-page/Author";

const CATEGORY = "dates";
const TKEY = "time-since";
const ROUTE_ENDING = "time-since";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

const DEFAULT_UNIT = "days";

import { getBaseUrl } from "~/helpers/getBaseUrl";

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta: MetaDescriptor[] = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      data?.meta.lastModified ? new Date(data.meta.lastModified) : null,
      schemaOrg.authorAlexFedorov
    ),
    // Open graph
    { property: "og:title", content: makeTitle(data?.meta.title) },
    { property: "og:type", content: "website" },
    { property: "og:url", content: data?.meta.currentUrl },
    // { property: "og:image", content: data?.meta.images[0] ?? "" },
    { property: "og:description", content: data?.meta.description },
    { property: "og:locale", content: data?.meta.locale }
  ];

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  addNoindexMeta(meta, data.meta.shouldIndex);

  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"],
  i18n: ["dates", "dates.time-since"]
};

interface Query {
  moment: Date;
  name: string | null;
  units: string;
  withTime: boolean;
}


function formatDate(date: Date, useUTC: boolean = false): string {
  const pad = (n: number): string => n.toString().padStart(2, "0"); // Helper function to pad numbers to 2 digits

  if (useUTC) {
    // Use UTC components for non-hydrated state (server-side rendering)
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T` +
      `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  } else {
    // Use local timezone components for hydrated state (client-side rendering)
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }
}

/**
 * Format date in UTC to ensure consistent timezone handling between server and client
 */
function formatDateUTC(date: Date): string {
  return formatDate(date, true);
}

function generateEventString(from: Date, name: string | null, locale: string, useUTC: boolean = false): string {
  // For dates displayed in the UI, we need to be careful about how we format them
  // to avoid hydration mismatches
  let dateString;
  
  if (useUTC) {
    // Use a stable UTC-based representation for server-side rendering
    dateString = new Date(Date.UTC(
      from.getUTCFullYear(),
      from.getUTCMonth(),
      from.getUTCDate()
    )).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  } else {
    // Use local timezone for client-side rendering
    dateString = from.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }
  
  // Common post-processing
  dateString = dateString
    .replace(/ /g, "\u00A0") // Replace spaces with non-breaking spaces
    .replace("г.", "") // Optional: Remove "г." if needed
    .trim(); // Remove any unwanted trailing/leading spaces
    
  return name ? `${name} (${dateString})` : dateString;
}

function getQueryFromURL(url: URL): Query | null {
  const lastPathPart = url.pathname.split("/").pop();
  invariant(lastPathPart);

  if (lastPathPart === ROUTE_ENDING) {
    return null;
  }

  // Parse the date exactly as provided in the URL without timezone adjustments
  // Using the ISO date string directly from the URL to avoid timezone issues
  const dateString = lastPathPart;
  let date;
  
  try {
    // Try to parse as a date - this will interpret it in local timezone
    // But we'll store it as an ISO string to maintain consistency
    date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return null;
    }
  } catch (e) {
    return null;
  }

  // Parse a query from the slug
  const query: Query = {
    moment: date,
    name: url.searchParams.get("name") ?? null,
    units: url.searchParams.get("units") ?? DEFAULT_UNIT,
    withTime: url.searchParams.get("withTime") === "true"
  };

  console.debug("Parsed query", query);

  return query;
}


export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";
  const url = new URL(request.url);
  const query: Query | null = getQueryFromURL(url);

  const t = await getTranslator(request, "dates.time-since");

  // const title = query ? createTitleFromQuery(query, locale, t, TKEY) : t(`${TKEY}.title`);
  const title = query !== null
    ? t("title_result_page", { event: generateEventString(query.moment, query.name, locale, true) })
    : t(`title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: t("title"), to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];


  if (query !== null) {
    breadcrumbs.push({ title, to: "" });
  }

  const mainCalculatorURL = getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING;

  // Create a consistent default date (beginning of current year in UTC)
  const currentYear = new Date().getUTCFullYear();
  const beginningOfYear = new Date(Date.UTC(currentYear, 0, 1, 0, 0, 0, 0)); // January 1st of current year at 00:00:00 UTC
  
  // Check if this is the main calculator page (no date in URL)
  const isMainCalculatorPage = query === null;
  
  return {
    baseUrl: mainCalculatorURL,
    defaultFrom: beginningOfYear.toISOString(),
    // Use consistent UTC time for server-side now
    serverSideNow: (new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), 0, 0, 0, 0))).toISOString(),
    query: query !== null ? {
      moment: query.moment.toISOString(),
      name: query.name,
      units: query.units,
      withTime: query.withTime
    } : null,
    meta: {
      locale,
      title: t(`title`),
      description: t(`description_short`),
      /**
       * Results page should have a canonical link to the main calculator page.
       * Attempt to glue links juice from results page to the main calculator page,
       * and avoid Google spam content filter
       */
      canonical: mainCalculatorURL,
      breadcrumbs,
      currentUrl: request.url,
      shouldIndex: {
        googlebot: isMainCalculatorPage,
        yandex: isMainCalculatorPage,
        bingbot: isMainCalculatorPage,
      },
      lastModified: LAST_UPDATED.toISOString()
    }
  };
};

export default function Index() {
  const {
    baseUrl,
    defaultFrom,
    serverSideNow,
    query
  } = useLoaderData<typeof loader>();
  const locale = getLocale(useLocation().pathname);
  invariant(locale);

  const { t } = useTranslation("dates.time-since");

  const isMainCalculatorPage = query === null;

  // Initialize with date string from server, but don't convert to Date object yet to avoid timezone adjustments
  const initialFromStr = query ? query.moment : defaultFrom;
  const [from, setFrom] = useState<Date>(new Date(initialFromStr));
  const fromRef = useRef<Date>(from);

  const [units, setUnits] = useState<string>(query ? query.units : DEFAULT_UNIT);
  const [name, setName] = useState<string | null>(query ? query.name : null);
  const [withTime, setWithTime] = useState<boolean>(query ? query.withTime : false);

  // Use server-side date objects initially for consistent hydration
  const [diff, setDiff] = useState<DateDiff>(getDatesDiff(new Date(serverSideNow), from));
  
  // Make sure we have the isHydrated state defined
  // Track if we've hydrated to know when to switch to client-side dates
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isFormOpened, setIsFormOpened] = useState<boolean>(isMainCalculatorPage);

  const link = () => {
    const params = new URLSearchParams();

    if (name) {
      params.append("name", name);
    }

    if (name) {
      params.append("units", units);
    }

    if (withTime) {
      params.append("withTime", "true");
    }

    // Use UTC format for server-side rendering, and local format for client-side
    // This ensures the generated link matches what's displayed in the address bar
    return `${baseUrl}/${formatDate(from, !isHydrated)}${params.toString() ? `?${params.toString()}` : ""}`;
  };

  useEffect(() => {
    // Mark that we're hydrated now
    setIsHydrated(true);
    
    // After hydration, update the diff once with client time
    setDiff(getDatesDiff(new Date(), fromRef.current));
    
    const interval = setInterval(() => {
      setDiff(getDatesDiff(new Date(), fromRef.current));
    }, 1000);

    return () => clearInterval(interval);
  }, []);
  
  // This effect updates the diff when the 'from' date changes
  useEffect(() => {
    setDiff(getDatesDiff(new Date(), from));
    fromRef.current = from;
  }, [from]);

  const unitOptions = [
    { name: t("input_units_minutes"), value: "minutes" },
    { name: t("input_units_hours"), value: "hours" },
    { name: t("input_units_days"), value: "days" },
    { name: t("input_units_months"), value: "months" },
    { name: t("input_units_years"), value: "years" }
  ];

  const eventString = useMemo(
    () => generateEventString(from, name, locale, !isHydrated),
    [from, name, locale, isHydrated]
  );

  const total = (() => {
    switch (units) {
      case "minutes":
        return diff.totalMinutes;
      case "hours":
        return diff.totalHours;
      case "days":
        return diff.totalDays;
      case "months":
        return diff.totalMonths;
      case "years":
        return diff.totalYears;
      default:
        return 0;
    }
  })();

  const permalink = link();
  const permalinkTitle = t("title_result_page", {
    event: eventString
  });

  return (<>
    <CalculatorLayout>
      {/* If opened by results link than results page should be shown first and form should be hidden*/}
      {!isMainCalculatorPage && <>
        <TimeSince
          total={total}
          t={t}
          diff={diff}
          event={eventString}
          units={units}
        />
      </>}

      {!isMainCalculatorPage && <>
        <div className="text-center text-sm">
          {isFormOpened
            ? <button
              className="border-dashed border-b dark:hover:text-white cursor-pointer"
              onClick={() => setIsFormOpened(false)}>{t("btn_hide_settings")}</button>
            : <button
              className="border-dashed border-b dark:hover:text-white cursor-pointer"
              onClick={() => setIsFormOpened(true)}>{t("btn_configure")}</button>
          }
        </div>
      </>}

      {isFormOpened && <>
        <ToolHeaderNew
          name={t(`title`)}
          description={t(`description`)}
        />

        <ToolForm className="items-start">
          <div
            className={clsx(
              "flex flex-col md:flex-row flex-wrap gap-4 w-full justify-center items-start md:px-6",
              "bg-slate-200 dark:bg-slate-700 rounded-sm text-lg"
            )}
          >
            <div className="flex flex-col gap-4 w-full xl:w-auto xl:max-w-[280px]">
              <FormGroup>
                <Label text={t(`which_date`)} />
                <DatePicker
                  name="date"
                  value={from}
                  ariaLabel={t(`input_date`)}
                  onChange={(value) => {
                    if (from.getTime() === value.getTime()) return;
                    setFrom(value);
                    if (!isTouched) setIsTouched(true);
                  }}
                  timepicker={withTime}
                  maxDate={new Date()}
                />
              </FormGroup>
              <FormGroup>
                <ToggleInput
                  label={t(`input_with_time`)}
                  checked={withTime}
                  onChange={setWithTime}
                />
              </FormGroup>
            </div>

            <div className="w-full xl:w-auto xl:min-w-[280px]">
              <FormGroup>
                <Label text={t(`input_name`)} />

                <TextInput
                  name="date"
                  type={"text"}
                  value={name ?? ""}
                  ariaLabel={t(`input_name`)}
                  placeholder={t("input_name_placeholder")}
                  onChange={(value) => {
                    setName(value.length > 0 ? value : null);
                    if (!isTouched) setIsTouched(true);
                  }}
                />
              </FormGroup>
            </div>

            <div className="w-full xl:w-auto">
              <FormGroup>
                <Label text={t(`input_units`)} />

                <SelectInput
                  name="unitA"
                  options={unitOptions}
                  value={unitOptions.find((unit) => unit.value === (units ?? DEFAULT_UNIT))?.value}
                  ariaLabel={t(`${TKEY}.unit`)}
                  onChange={(value) => {
                    setUnits(value);
                    if (!isTouched) setIsTouched(true);
                  }}
                />
              </FormGroup>
            </div>
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="text-sm pb-3">{t("link_to_result")}</div>
            <div className="flex flex-col gap-3 justify-center items-center text-xs break-all max-w-[600px]">
              <a href={permalink} rel="nofollow" className="border-none">{permalink}</a>
              <div className="flex flex-col md:flex-row gap-2 w-auto text-center">
                <Button
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(permalink)}
                >
                  {t("link_copy")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(
                    "<a href=\"" + permalink + "\" target='_blank'>" + permalinkTitle + "</a>"
                  )}
                >
                  {t("link_copy_html")}
                </Button>
              </div>
            </div>
          </div>
        </ToolForm>
      </>}

      {isMainCalculatorPage && isTouched && <>
        <TimeSince total={total} t={t} diff={diff} event={eventString} units={units} />
      </>}
    </CalculatorLayout>

    {isMainCalculatorPage && <>
      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection id="what-is-it" contentsText={t("info_what_is_it_contents_text")} contentsEmoji="❓">
            <H2>{t("info_h_what_is_it")}</H2>
            <p>{t("info_p_what_is_it")}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="when-useful" contentsText={t("info_when_useful_contents_text")} contentsEmoji="💡">
            <H2>{t("info_h_when_useful")}</H2>
            <p>{t("info_p_when_useful_1")}</p>
            <p>{t("info_p_when_useful_4")}</p>
            <ul className="list-disc pl-5">
              <li>{t("info_li_blogs")}</li>
              <li>{t("info_li_chats")}</li>
              <li>{t("info_li_portals")}</li>
              <li>{t("info_li_memory_dates")}</li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="features" contentsText={t("info_features_contents_text")} contentsEmoji="⚙️">
            <H2>{t("info_h_features")}</H2>
            <ul className="list-disc pl-5">
              <li>{t("info_li_years")}</li>
              <li>{t("info_li_months")}</li>
              <li>{t("info_li_days")}</li>
              <li>{t("info_li_hours")}</li>
              <li>{t("info_li_minutes")}</li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="how-to-use" contentsText={t("info_how_to_use_contents_text")} contentsEmoji="📘">
            <H2>{t("info_h_how_to_use")}</H2>
            <ol className="list-decimal pl-5">
              <li>{t("info_step_1")}</li>
              <li>{t("info_step_2")}</li>
              <li>{t("info_step_3")}</li>
              <li>{t("info_step_4")}</li>
              <li>{t("info_step_5")}</li>
              <li>{t("info_step_6")}</li>
            </ol>
          </AdditionalInfoSection>
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>}
  </>);
}

